import { CdkDrag, CdkDragDrop, CdkDropList } from "@angular/cdk/drag-drop";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  output,
  Signal,
  signal,
  viewChildren
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";

import { filter } from "rxjs/operators";

import { DocumentContextMenuComponent } from "@documentation/components/sidebar/tabs/documents-list/document-tree/document-context-menu/document-context-menu.component";
import { DocumentDuplicatePayload } from "@documentation/components/sidebar/tabs/documents-list/document-tree/document-context-menu/document-context-menu.component";
import { FolderNodeComponent } from "@documentation/components/sidebar/tabs/documents-list/document-tree/folder-node/folder-node.component";
import { DocumentListItemModel } from "@documentation/models/document.model";
import { FolderModel, TreeNode } from "@documentation/models/folder.model";
import { DocumentationRoutesConstants } from "@documentation/routes/documentation.routes.constants";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { ClickOrDoubleClickDirective } from "@shared/directives/click-or-double-click.directive";

export enum DocumentTreeMode {
  Default,
  Picker
}

@Component({
  selector: "component-document-tree",
  imports: [
    DocumentContextMenuComponent,
    CdkDrag,
    CustomIconComponent,
    CdkDropList,
    FolderNodeComponent,
    ClickOrDoubleClickDirective
  ],
  templateUrl: "./document-tree.component.html",
  styleUrl: "./document-tree.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "(window:mouseup)": "isMouseDown.set(false)"
  }
})
export class DocumentTreeComponent implements OnInit {
  readonly items = input.required<TreeNode[]>();
  readonly folderIds = input.required<string[]>();
  readonly isPublicView = input.required<boolean>();
  readonly folderId = input.required<string>();
  readonly disableDrag = input.required<boolean>();
  readonly disableMove = input.required<boolean>();
  readonly expandAll = input.required<boolean>();
  readonly collapsed = input.required<boolean>();
  readonly isInPublicFolder = input.required<boolean>();
  readonly isCreateDisabled = input.required<boolean>();
  readonly hasContextMenu = input.required<boolean>();
  readonly allowPublicBadge = input.required<boolean>();
  readonly mode = input.required<keyof typeof DocumentTreeMode>();

  readonly isLoading = input<boolean>(false);
  readonly emptyMessage = input<string | null>();
  readonly selectedDocumentsMap = model<Map<string, DocumentListItemModel>>();

  readonly entityUpdated = output<TreeNode>();
  readonly documentDeleted = output<DocumentListItemModel>();
  readonly duplicateDocument = output<DocumentDuplicatePayload>();
  readonly folderDelete = output<FolderModel>();
  readonly drop = output<CdkDragDrop<TreeNode[]>>();
  readonly entityMoved = output<TreeNode>();
  readonly treeUpdated = output<void>();
  readonly documentCreate = output<FolderModel>();

  protected readonly hasChildMouseDown: Signal<boolean>;

  protected readonly isMouseDown = signal<boolean>(false);

  protected readonly isPickerMode = computed(() => DocumentTreeMode[this.mode()] === DocumentTreeMode.Picker);
  protected readonly isDefaultMode = computed(() => DocumentTreeMode[this.mode()] === DocumentTreeMode.Default);

  protected readonly cm = viewChildren(DocumentContextMenuComponent);
  private readonly _folderNodes = viewChildren(FolderNodeComponent);
  private readonly _router = inject(Router);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);

  constructor() {
    this.hasChildMouseDown = computed(() => this.hasChildMouseDownComputation());
  }

  ngOnInit(): void {
    /*
     * This ensures the active document indication is re-evaluated after the route changes.
     * Fixes the case where the active document indication is not updated
     * after closing the "Unsaved changes" modal.
     */
    if (this.isDefaultMode()) {
      this._router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          takeUntilDestroyed(this._destroyRef)
        )
        .subscribe(() => this._cdr.markForCheck());
    }
  }

  protected getDocumentLink(id: string): string[] {
    if (this.isPublicView()) {
      return [DocumentationRoutesConstants.publicDocument(id, this.folderId())];
    }

    return [DocumentationRoutesConstants.document(id)];
  }

  protected isDocumentActive(id: string): boolean {
    const link = this.getDocumentLink(id);
    return this._router.isActive(this._router.createUrlTree(link), {
      paths: "exact",
      queryParams: "ignored",
      fragment: "ignored",
      matrixParams: "ignored"
    });
  }

  protected evenPredicate(item: CdkDrag<TreeNode>): boolean {
    return item.data.type === "document";
  }

  protected onMouseDown(event: MouseEvent): void {
    if (event.button === 0) {
      this.isMouseDown.set(true);
    }
  }

  protected onDragStarted(): void {
    document.body.classList.add("dragging");
  }

  protected onDragEnded(): void {
    document.body.classList.remove("dragging");
  }

  protected onDocumentSingleClick(document: DocumentListItemModel, _event: MouseEvent): void {
    switch (this.mode()) {
      case "Default":
        const link = this.getDocumentLink(document.id);
        this._router.navigate(link);
        break;
      case "Picker":
        this.onToggleSelection(document);
        break;
    }
  }

  protected onDocumentDoubleClick(documentId: string, event: MouseEvent): void {
    if (this.hasContextMenu()) {
      const contextMenu = this.cm().find((menu) => menu.entity()?.id === documentId);
      contextMenu?.onRenameClick(event);
    }
  }

  protected onToggleSelection(document: DocumentListItemModel): void {
    const updated = new Map(this.selectedDocumentsMap());
    const id = document.id;

    updated.has(id) ? updated.delete(id) : updated.set(id, document);

    this.selectedDocumentsMap.set(updated);
  }

  private hasChildMouseDownComputation(): boolean {
    return this._folderNodes().some((folder) => {
      const tree = folder.tree();

      return tree?.isMouseDown() || tree?.hasChildMouseDown();
    });
  }
}
